import { Component, OnInit, ChangeDetectionStrategy, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomErrors } from '@asamco/base';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { LinkSettingView } from '../../interfaces/link-setting-view.interface';
import { DescriptionModalComponent } from '../description-modal/description-modal.component';
import { AsValidatorDirective } from '../../../../../../base/src/lib/as-validator/as-validator.directive';
import {
	DxSwitchModule,
	DxDateBoxModule,
	DxNumberBoxModule,
	DxLookupModule,
	DxTextBoxModule,
	DxTextAreaModule,
	DxDropDownBoxModule,
	DxDataGridModule,
	DxCheckBoxModule,
	DxButtonModule,
	DxPopupModule,
} from 'devextreme-angular';
import { NgIf, NgSwitch, NgSwitchCase, AsyncPipe, NgFor } from '@angular/common';
import dxNumberBox from 'devextreme/ui/number_box';
import { SettingDropdownComponent } from '../setting-dropdown/setting-dropdown.component';
import { DataType } from '../../enums/data-type.enum';

export class DropDownDatasourceRow {
	Id: number;
	Value: string;
	Selected: boolean;
}

@Component({
	selector: 'al-link-edit-settings',
	templateUrl: './link-edit-settings.component.html',
	styleUrls: ['./link-edit-settings.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		NgSwitch,
		NgFor,
		FormsModule,
		ReactiveFormsModule,
		NgSwitchCase,
		DxSwitchModule,
		AsValidatorDirective,
		DxDateBoxModule,
		DxNumberBoxModule,
		DxLookupModule,
		DxTextBoxModule,
		DxTextAreaModule,
		SettingDropdownComponent,
		AsyncPipe,
		DxButtonModule,
		DxDataGridModule,
		DxPopupModule,
	],
})
export class LinkEditSettingsComponent implements OnInit, OnDestroy {
	/** Custom validation error messages */
	public customErrors: CustomErrors = {
		ERROR_REQUIRED: 'This field is required',
		ERROR_MAXLENGTH: 'This field exceeds the maximum allowed amount of characters',
	};

	readonly checkboxItems = [
		{ text: 'On', key: 1 },
		{ text: 'Off', key: 0 },
	];

	isMapType(): boolean {
		return this.setting.dataType === DataType.Map || this.setting.dataType === DataType.EditableMap;
	}
	isMapEditable(): boolean {
		return this.setting.dataType === DataType.EditableMap;
	}
	public gridMapVisible: boolean = false;

	@Input()
	setting: LinkSettingView;

	@Input()
	form: FormGroup;

	@Input()
	descriptionModal: DescriptionModalComponent;

	@Input()
	dateFormat: string;

	public showGridMap() {
		this.gridMapVisible = true;
	}

	private destroy$ = new Subject<void>();

	public dataSource: any[] = [];
	public dataSourceSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

	constructor(private cdr: ChangeDetectorRef) {}

	onGridSaving(event: any) {
		const changes = event.changes; // Contains added/updated/deleted rows
		const formArray = this.form.controls[this.setting.id]; // Type assertion here
		const currentValue = JSON.parse(formArray.value) || []; // Extract current values

		// Iterate through the changes using a for loop
		for (let i = 0; i < changes.length; i++) {
			const change = changes[i];

			if (change.type === 'insert') {
				currentValue.push(change.data); // Add the new row
			} else if (change.type === 'update') {
				for (let j = 0; j < currentValue.length; j++) {
					if (currentValue[j].Key === change.key.Key && currentValue[j].Value === change.key.Value) {
						// Update the existing row with new data
						currentValue[j] = { ...currentValue[j], ...change.data };
						break; // Exit loop once updated
					}
				}
			} else if (change.type === 'remove') {
				for (let j = 0; j < currentValue.length; j++) {
					if (currentValue[j].Key === change.key.Key && currentValue[j].Value === change.key.Value) {
						currentValue.splice(j, 1); // Remove the row
						break; // Exit loop once removed
					}
				}
			}
		}

		// Update the form control with the new value
		formArray.setValue(JSON.stringify(currentValue));
		formArray.markAsDirty(); // Mark it as changed
	}

	ngOnInit(): void {
		if (this.setting.dataSource) {
			this.dataSource = JSON.parse(this.setting.dataSource);
			this.dataSourceSubject.next(this.dataSource);
		}
		if (this.setting.dataType == DataType.Map || this.setting.dataType == DataType.EditableMap) {
			this.dataSource = JSON.parse(this.setting.useValue);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	showDescription(): void {
		this.descriptionModal
			.show(this.setting)
			.pipe(takeUntil(this.destroy$))
			.subscribe(result => {
				if (result.type == 'description') {
					this.setting.settingDescription = result.content;
					this.cdr.detectChanges();
				} else if (result.type == 'title') {
					this.setting.displayName = result.content;
					console.log(this.setting);
					this.cdr.detectChanges();
				}
			});
	}

	prepareDropdownDs(dropdownDatasource: DropDownDatasourceRow[], settingValue: string) {
		let selectedValues;
		try {
			selectedValues = JSON.parse(settingValue);
		} catch {
			selectedValues = [];
		}
		for (const row of dropdownDatasource) {
			row.Selected = selectedValues.includes(row.Id);
		}
		return dropdownDatasource;
	}

	onDropdownSelectionChanged(settingValue: string) {
		this.form.controls[this.setting.id].setValue(settingValue);
		this.form.controls[this.setting.id].markAsDirty();
	}
}
